// Simple TMDb client for browser usage
// Requires REACT_APP_TMDB_API_KEY in environment

const API_KEY = process.env.REACT_APP_TMDB_API_KEY || process.env.NEXT_PUBLIC_TMDB_API_KEY;
const V4_TOKEN = process.env.REACT_APP_TMDB_V4_TOKEN || process.env.NEXT_PUBLIC_TMDB_V4_TOKEN;
const API_BASE = 'https://api.themoviedb.org/3';
export const IMG_BASE = 'https://image.tmdb.org/t/p/';

function hasKey() {
  return (!!API_KEY && API_KEY !== 'YOUR_TMDB_KEY_HERE') || !!V4_TOKEN;
}

function buildUrl(path, params = {}) {
  const usp = new URLSearchParams(params);
  // For v3 api_key mode only add when not using v4 token
  if (!V4_TOKEN && API_KEY) usp.set('api_key', API_KEY);
  const base = path.startsWith('http') ? '' : API_BASE;
  return `${base}${path}?${usp.toString()}`;
}

function buildOpts() {
  if (V4_TOKEN) {
    return {
      headers: {
        'accept': 'application/json',
        'Authorization': `Bearer ${V4_TOKEN}`
      }
    };
  }
  return { headers: { 'accept': 'application/json' } };
}

async function doFetch(url) {
  const r = await fetch(url, buildOpts());
  if (!r.ok) {
    let msg = `${r.status} ${r.statusText}`;
    try {
      const j = await r.json();
      if (j && (j.status_message || j.message)) msg = j.status_message || j.message;
    } catch {}
    throw new Error(msg);
  }
  return r.json();
}

export async function searchTV(query, { language = 'cs-CZ' } = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl('/search/tv', { query, language, include_adult: 'false' });
  const j = await doFetch(url);
  return Array.isArray(j.results) ? j.results : [];
}

export async function getTV(tvId, { language = 'cs-CZ' } = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl(`/tv/${tvId}`, { language });
  return await doFetch(url);
}

export async function getSeason(tvId, seasonNumber, { language = 'cs-CZ' } = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl(`/tv/${tvId}/season/${seasonNumber}`, { language });
  return await doFetch(url);
}

export async function getSeasonWithFallback(tvId, seasonNumber) {
  // Try Czech, then English fallback per-episode with flags
  const cz = await getSeason(tvId, seasonNumber, { language: 'cs-CZ' }).catch(() => null);
  const en = await getSeason(tvId, seasonNumber, { language: 'en-US' }).catch(() => null);
  if (!cz && !en) throw new Error('TMDb season not available');
  const base = cz || en;
  const episodes = (base.episodes || []).map((baseEp) => {
    const epNo = baseEp.episode_number;
    const czEp = (cz && Array.isArray(cz.episodes)) ? cz.episodes.find(e => e.episode_number === epNo) : null;
    const enEp = (en && Array.isArray(en.episodes)) ? en.episodes.find(e => e.episode_number === epNo) : null;
    const hadCzName = !!(czEp?.name && czEp.name.trim());
    const hadCzOverview = !!(czEp?.overview && czEp.overview.trim());
    const name = hadCzName ? czEp.name : (enEp?.name || `Epizoda ${epNo}`);
    const overview = hadCzOverview ? czEp.overview : (enEp?.overview || '');
    const still = (czEp?.still_path) || (enEp?.still_path) || '';
    return {
      ...baseEp,
      name,
      overview,
      still_path: still,
      czNameMissing: !hadCzName && !!(enEp?.name),
      czOverviewMissing: !hadCzOverview && !!(enEp?.overview),
      en_name: enEp?.name || '',
      en_overview: enEp?.overview || ''
    };
  });
  return { ...base, episodes };
}

export function buildImageUrl(path, size = 'w500') {
  if (!path) return '';
  // size examples: w300, w500, w780, original
  return `${IMG_BASE}${size}${path}`;
}

// Episode translations (to reduce reliance on external translators)
export async function getEpisodeTranslations(tvId, seasonNumber, episodeNumber) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl(`/tv/${tvId}/season/${seasonNumber}/episode/${episodeNumber}/translations`, {});
  return await doFetch(url);
}

export async function getEpisodeTranslationCS(tvId, seasonNumber, episodeNumber) {
  const data = await getEpisodeTranslations(tvId, seasonNumber, episodeNumber).catch(() => null);
  const arr = (data && Array.isArray(data.translations)) ? data.translations : [];
  const cs = arr.find(t => t.iso_639_1 === 'cs') || arr.find(t => t.iso_639_1 === 'sk');
  if (cs && cs.data) {
    return {
      name: cs.data.name || '',
      overview: cs.data.overview || ''
    };
  }
  return null;
}

// Movies
export async function searchMovie(query, { language = 'cs-CZ' } = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl('/search/movie', { query, language, include_adult: 'false' });
  const j = await doFetch(url);
  return Array.isArray(j.results) ? j.results : [];
}

export async function getMovie(movieId, { language = 'cs-CZ' } = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const url = buildUrl(`/movie/${movieId}`, { language });
  return await doFetch(url);
}

export async function getMovieWithFallback(movieId) {
  const cz = await getMovie(movieId, { language: 'cs-CZ' }).catch(() => null);
  const en = await getMovie(movieId, { language: 'en-US' }).catch(() => null);
  const base = cz || en;
  if (!base) throw new Error('TMDb movie not available');
  const hadCzTitle = !!(cz?.title && cz.title.trim());
  const hadCzOverview = !!(cz?.overview && cz.overview.trim());
  const title = hadCzTitle ? cz.title : (en?.title || '');
  const overview = hadCzOverview ? cz.overview : (en?.overview || '');
  return { ...base, title, overview, czTitleMissing: !hadCzTitle && !!(en?.title), czOverviewMissing: !hadCzOverview && !!(en?.overview), enTitle: en?.title || '', enOverview: en?.overview || '' };
}

// Images (posters/backdrops)
export async function getTVImages(tvId, opts = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const params = { include_image_language: 'cs,null,en' };
  const url = buildUrl(`/tv/${tvId}/images`, params);
  return await doFetch(url);
}

export async function getMovieImages(movieId, opts = {}) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const params = { include_image_language: 'cs,null,en' };
  const url = buildUrl(`/movie/${movieId}/images`, params);
  return await doFetch(url);
}

export async function getEpisodeImages(tvId, seasonNumber, episodeNumber) {
  if (!hasKey()) throw new Error('TMDb key missing');
  const params = { include_image_language: 'cs,null,en' };
  const url = buildUrl(`/tv/${tvId}/season/${seasonNumber}/episode/${episodeNumber}/images`, params);
  return await doFetch(url);
}
